/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";

import { DeliveryMode, OrderStatus, OrderStatusType } from "../storage/types";

export const colorMap = {
  orange: { text: colors.shade0, bg: colors.secondary700 },
  green: { text: colors.shade100, bg: colors.primary300 },
  grey: { text: colors.grey900, bg: colors.grey400 },
  black: { text: colors.shade0, bg: colors.grey800 },
};

const days14 = 14 * 24 * 60 * 60 * 1000;

export const mapStatus = (status: OrderStatusType, mode: string, createdAt: Date) => {
  const now = new Date();
  const is14DaysPassed = now.getTime() - new Date(createdAt).getTime() > days14;
  switch (status) {
    case OrderStatus.Created:
    case OrderStatus.OpenOrder:
      return {
        display: "Order received",
        color: colorMap["orange"],
      };
    case OrderStatus.InProcessing:
    case OrderStatus.Backordered:
    case OrderStatus.PartiallyPicked:
    case OrderStatus.Pending:
    case OrderStatus.Picked:
      return {
        display: "Processing",
        color: colorMap["orange"],
      };
    case OrderStatus.Delivered:
    case OrderStatus.Invoiced: {
      if (is14DaysPassed) {
        return undefined;
      }

      if (mode === DeliveryMode.Pickup) {
        return {
          display: "Ready for pickup",
          color: colorMap["green"],
        };
      } else if (mode === DeliveryMode.Delivery) {
        return {
          display: "Shipped",
          color: colorMap["green"],
        };
      } else {
        return undefined;
      }
    }
    case OrderStatus.PartiallyDelivered:
    case OrderStatus.PartiallyInvoiced: {
      return {
        display: mode === DeliveryMode.Pickup ? "Partially ready for pickup" : "Partially shipped",
        color: colorMap["green"],
      };
    }
    case OrderStatus.Canceled:
    case OrderStatus.Returned:
      return {
        display: "Cancelled",
        color: colorMap["grey"],
      };
    case OrderStatus.OnHold:
    case OrderStatus.FraudHold:
      return {
        display: "On hold",
        color: colorMap["grey"],
      };
  }
  if (mode === DeliveryMode.None) {
    return {
      display: "Purchase made in store",
      color: colorMap["black"],
    };
  }
  return {
    display: "TBC",
    color: colorMap["orange"],
  };
};

export const mapLineStatus = (status: OrderStatusType, mode: string, createdAt: Date) => {
  const now = new Date();
  const is14DaysPassed = now.getTime() - new Date(createdAt).getTime() > days14;

  switch (status) {
    case OrderStatus.AtDirectShipper:
    case OrderStatus.InProcessing:
    case OrderStatus.Backordered:
    case OrderStatus.PartiallyPicked:
    case OrderStatus.PartiallyDelivered:
    case OrderStatus.PartiallyExchanged:
    case OrderStatus.PartiallyInvoiced:
    case OrderStatus.Picked:
      return {
        display: "Processing",
        color: colorMap["orange"],
      };
    case OrderStatus.OpenLine:
      return {
        display: "Pending processing",
        color: colorMap["orange"],
      };
    case OrderStatus.Delivered:
    case OrderStatus.Invoiced: {
      if (is14DaysPassed) {
        return undefined;
      }

      if (mode === DeliveryMode.Pickup) {
        return {
          display: "Ready for pickup",
          color: colorMap["green"],
        };
      } else if (mode === DeliveryMode.Delivery) {
        return {
          display: "Ready to ship",
          color: colorMap["green"],
        };
      } else {
        return undefined;
      }
    }
    case OrderStatus.ToBeDirectDelivered:
      return {
        display: "To be delivered by supplier",
        color: colorMap["green"],
      };
    case OrderStatus.CustomerCanceled:
    case OrderStatus.SystemCanceled:
      return {
        display: "Cancelled",
        color: colorMap["grey"],
      };
    case OrderStatus.OnHold:
    case OrderStatus.FraudHold:
      return {
        display: "On hold",
        color: colorMap["grey"],
      };
  }
  return undefined;
};
